'use client';

import { useMemo, useState } from 'react';
import {
  runPanelQuiz,
  type Construction,
  type RoomType,
} from '@/lib/tools/panelQuiz';
import type { LengthUnit } from '@/lib/tools/units';
import { CONTENT, CONTENT_NAME, EXPLANATION, SECOND_LINE } from '@/components/seo/roles';
import { optionCardClass } from '@/components/room/chrome/selection';
import Button from '@/components/ui/Button';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import ToolMetricCard from '@/components/tools/ToolMetricCard';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';

const ROOM_TYPES: { id: RoomType; label: string; blurb: string }[] = [
  { id: 'mixing_studio', label: 'Mixing studio', blurb: 'Tight, controlled decay' },
  { id: 'home_theater', label: 'Home theater', blurb: 'Cinema impact + clarity' },
  { id: 'hifi', label: 'Hi-Fi listening', blurb: 'Natural space, even bass' },
];

const CONSTRUCTION: { id: Construction; label: string }[] = [
  { id: 'drywall', label: 'Drywall' },
  { id: 'concrete', label: 'Concrete' },
  { id: 'glass', label: 'Glass-heavy' },
];

export default function PanelCoverageQuizWidget() {
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [step, setStep] = useState(0);
  const [lengthM, setLengthM] = useState(5);
  const [widthM, setWidthM] = useState(4);
  const [heightM, setHeightM] = useState(2.6);
  const [roomType, setRoomType] = useState<RoomType>('mixing_studio');
  const [construction, setConstruction] = useState<Construction>('drywall');

  const result = useMemo(
    () => runPanelQuiz({ lengthM, widthM, heightM, roomType, construction }),
    [lengthM, widthM, heightM, roomType, construction],
  );

  const lead = `${result.breakdown.cornerBassTraps} corner bass traps (≈125 Hz) + ${result.breakdown.firstReflectionPanels} first-reflection panels (500 Hz–4 kHz) + ${result.breakdown.ceilingClouds} ceiling clouds.`;

  return (
    <div className="space-y-8" data-testid="panel-quiz">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>
      <ol className="flex gap-2 text-caption text-[var(--rt-ink-dim)]">
        {['Room size', 'Room type', 'Construction', 'Results'].map((label, i) => (
          <li
            key={label}
            className={
              'rounded-full px-3 py-1 border ' +
              (i === step
                ? 'border-[var(--rt-accent)] text-[var(--rt-accent)]'
                : i < step
                  ? 'border-[var(--app-border-bright)] text-[var(--rt-ink)]'
                  : 'border-[var(--app-border)]')
            }
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="space-y-4">
          <p className={EXPLANATION}>How big is the room?</p>
          <ToolDimSlider label="Length" valueM={lengthM} minM={3} maxM={12} stepM={0.1} unit={unit} onChangeM={setLengthM} />
          <ToolDimSlider label="Width" valueM={widthM} minM={2.5} maxM={10} stepM={0.1} unit={unit} onChangeM={setWidthM} />
          <ToolDimSlider label="Height" valueM={heightM} minM={2} maxM={5} stepM={0.1} unit={unit} onChangeM={setHeightM} />
          <Nav onNext={() => setStep(1)} nextTestId="quiz-next" />
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="quiz-step-room-type">
          <p className={EXPLANATION}>What is the room for?</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {ROOM_TYPES.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`quiz-room-${r.id}`}
                aria-label={r.label}
                onClick={() => setRoomType(r.id)}
                // SELECTION IS NOT AN ACTION: chosen reads as raised, never an
                // accent fill (AGENTS.md Button + selection rule; selection.ts).
                className={optionCardClass(roomType === r.id, 'flex-col items-start')}
              >
                <div className={CONTENT_NAME}>{r.label}</div>
                <div className={SECOND_LINE}>{r.blurb}</div>
              </button>
            ))}
          </div>
          <Nav onBack={() => setStep(0)} onNext={() => setStep(2)} nextTestId="quiz-next" />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="quiz-step-construction">
          <p className={EXPLANATION}>Main construction material?</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {CONSTRUCTION.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`quiz-construction-${c.id}`}
                aria-label={c.label}
                onClick={() => setConstruction(c.id)}
                className={optionCardClass(construction === c.id, 'items-center justify-center')}
              >
                <span className={CONTENT_NAME}>{c.label}</span>
              </button>
            ))}
          </div>
          <Nav
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextLabel="See results"
            nextTestId="quiz-see-results"
          />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6" data-testid="panel-quiz-results">
          <div className="grid sm:grid-cols-3 gap-3">
            <ToolMetricCard label="Volume" value={`${result.volumeM3} m³`} />
            <ToolMetricCard label="Baseline RT60" value={`${result.baselineRt60Sec.toFixed(2)} s`} />
            <ToolMetricCard label="Target RT60" value={`${result.targetRt60Sec.toFixed(2)} s`} />
          </div>
          <p className={SECOND_LINE}>
            Need about {result.requiredSabins} sabins of absorption (Sabine estimate from your
            construction).
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            <ToolMetricCard
              label="Corner bass traps"
              value={result.breakdown.cornerBassTraps}
              hint="≈125 Hz control"
              emphasized
            />
            <ToolMetricCard
              label="First-reflection panels"
              value={result.breakdown.firstReflectionPanels}
              hint="500 Hz – 4 kHz"
            />
            <ToolMetricCard
              label="Ceiling clouds"
              value={result.breakdown.ceilingClouds}
              hint="Early vertical paths"
            />
          </div>
          <p className={CONTENT}>
            Total: <span className="font-mono text-[var(--rt-ink)] font-semibold">{result.breakdown.totalPanels}</span>{' '}
            treatment pieces — not a generic “buy N panels” guess.
          </p>
          <Nav onBack={() => setStep(2)} />
          <ToolLeadIn result={lead} />
        </div>
      )}
    </div>
  );
}

function Nav({
  onBack,
  onNext,
  nextLabel = 'Next',
  nextTestId,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextTestId?: string;
}) {
  return (
    <div className="flex justify-between pt-2">
      {onBack ? (
        <Button variant="ghost" size="md" onClick={onBack} data-testid="quiz-back">
          Back
        </Button>
      ) : (
        <span />
      )}
      {onNext && (
        <Button size="md" data-testid={nextTestId} onClick={onNext}>
          {nextLabel}
        </Button>
      )}
    </div>
  );
}
